import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { set } from 'lodash';
import { saveToLocalStorage } from 'src/configuration/setup/localStorage';
import {
    IMerging,
    INewElements,
    ITranslationKeys,
    ITranslationsByLang,
    ITranslationsDocument,
    ITranslationsState,
    LanguageCode,
    TranslationKey,
} from 'src/features/translations/interfaces';
import { RootState } from 'src/configuration/setup/store';
import { LOCAL_STORAGE_KEY, MERGE_STATUS } from 'src/features/translations/constants';

const initialState: ITranslationsState = {
    document: {
        meta: {
            version: 0,
            lastModified: '',
        },
        translations: {},
    },
    initialTerms: {},
    uploadedTerms: {},
    selectedLanguages: [],
    enabledSaveButton: false,
    merging: {
        dataVersion: 0,
        translationVersion: 0,
        status: MERGE_STATUS.NOTSTARTED,
        complete: false,
    },
    newElements: {
        categories: [],
        terms: [],
    },
    translationKeys: {
        meta: { version: 0, lastModified: '' },
        data: [],
    },
};
/**
 * Saves the current translations in localStorage.
 * @param key - Key used by local storage
 * @param terms - Translations to save.
 */
const persistTermsToLocalStorage = (key: string, terms: object) => {
    // saveToLocalStorage('translations', JSON.stringify(terms));
    saveToLocalStorage(key, JSON.stringify(terms));
};

export const translationsSlice = createSlice({
    name: 'translations',
    initialState,
    reducers: {
        // UploadConfirmDialog
        clearUploadedTerms: (state) => {
            state.uploadedTerms = {};
        },
        // on leaving translations feature
        cleanupTranslations: () => {
            // persistTermsToLocalStorage(LOCAL_STORAGE_KEY.TRANSLATIONS, {});
            window.localStorage.removeItem(LOCAL_STORAGE_KEY.TRANSLATIONS);
        },
        // upload terms via file upload
        uploadTerms: (state, action: PayloadAction<ITranslationsByLang>) => {
            state.uploadedTerms = action.payload;
        },
        // overwrite existing terms with uploaded terms on confirmation
        overwriteTerms: (state) => {
            state.document.translations = state.uploadedTerms;
            state.uploadedTerms = {};
            state.enabledSaveButton = true;
            persistTermsToLocalStorage(LOCAL_STORAGE_KEY.TRANSLATIONS, state.document.translations);
        },
        setMerging: (state, action: PayloadAction<IMerging>) => {
            state.merging = action.payload;
            console.info(action.payload);
        },
        setTranslationKeys: (state, action: PayloadAction<ITranslationKeys>) => {
            state.translationKeys = action.payload;
        },
        // on click of country flag
        // on getting languages from local storage after refresh
        // on selecting languages from dropdown
        // set languages on component clean up
        setSelectedLanguages: (state, action: PayloadAction<string[]>) => {
            state.selectedLanguages = action.payload;
            persistTermsToLocalStorage(LOCAL_STORAGE_KEY.SELECTED_LANGUAGES, action.payload);
        },
        // on request translations via api from server
        setTranslations: (state, action: PayloadAction<ITranslationsDocument>) => {
            state.document.meta = action.payload.meta;
            state.document.translations = action.payload.translations;
            state.initialTerms = action.payload.translations;
            persistTermsToLocalStorage(LOCAL_STORAGE_KEY.INIT_TRANSLATIONS, action.payload);
        },
        // set translations after merging to update the UI with new merged translations and enable save button
        updateTranslationsWithMerge: (state, action: PayloadAction<ITranslationsDocument>) => {
            setInitialTranslations();
            state.document.translations = action.payload.translations;
            state.enabledSaveButton = true;
            persistTermsToLocalStorage(LOCAL_STORAGE_KEY.TRANSLATIONS, action.payload);
        },
        // set new terms and categories added via merge to highlight in the UI
        newTranslationElements: (state, action: PayloadAction<INewElements>) => {
            state.newElements = action.payload;
        },
        // reset translations to initial state
        resetTranslations: (state) => {
            state.document.translations = state.initialTerms;
            state.enabledSaveButton = false;
            persistTermsToLocalStorage(LOCAL_STORAGE_KEY.INIT_TRANSLATIONS, state.initialTerms);
            persistTermsToLocalStorage(LOCAL_STORAGE_KEY.TRANSLATIONS, {});
        },
        // on success notification
        setInitialTranslations: (state) => {
            state.initialTerms = state.document.translations;
            state.enabledSaveButton = false;
            persistTermsToLocalStorage(LOCAL_STORAGE_KEY.INIT_TRANSLATIONS, state.document.translations);
            persistTermsToLocalStorage(LOCAL_STORAGE_KEY.TRANSLATIONS, {});
        },
        // on change of term value
        updateTermProperties: (
            state,
            action: PayloadAction<{
                language: LanguageCode;
                expression: TranslationKey;
                value: string;
                category: string;
                status: number;
            }>
        ) => {
            const { language, expression, category, value } = action.payload;
            set(state.document.translations, [language, category, expression, 'value'], value);
            state.enabledSaveButton = true;
            persistTermsToLocalStorage(LOCAL_STORAGE_KEY.TRANSLATIONS, state.document.translations);
        },
        // on change of term status
        updateStatusProperties: (
            state,
            action: PayloadAction<{
                language: LanguageCode;
                expression: TranslationKey;
                category: string;
                status: number;
            }>
        ) => {
            const { language, expression, category, status } = action.payload;
            set(state.document.translations, [language, category, expression, 'status'], status);
            state.enabledSaveButton = true;
            persistTermsToLocalStorage(LOCAL_STORAGE_KEY.TRANSLATIONS, state.document.translations);
        },
        // on change of term status for one or all languages
        updateStatusPropertiesAll: (
            state,
            action: PayloadAction<{
                languages: LanguageCode[];
                expressions: TranslationKey[];
                category: string;
                status: number;
            }>
        ) => {
            const { languages, expressions, category, status } = action.payload;
            languages.forEach((language) => {
                expressions.forEach((expression) => {
                    if (
                        state.document.translations[language] &&
                        state.document.translations[language][category] &&
                        state.document.translations[language][category][expression]
                    ) {
                        set(state.document.translations, [language, category, expression, 'status'], status);
                    }
                });
            });
            state.enabledSaveButton = true;
            persistTermsToLocalStorage(LOCAL_STORAGE_KEY.TRANSLATIONS, state.document.translations);
        },
        enableSaveButton: (state, action: PayloadAction<boolean>) => {
            state.enabledSaveButton = action.payload;
        },
    },
});

export const getTranslations = (state: RootState) => state.translations;
export const getTranslationDocument = (state: RootState) => state.translations.document;
export const getTranslationTerms = (state: RootState) => state.translations.document.translations;
export const getSelectedLanguages = (state: RootState) => state.translations.selectedLanguages;
export const getSaveButtonEnabled = (state: RootState) => state.translations.enabledSaveButton;
export const getMerging = (state: RootState) => state.translations.merging;
export const getNewCategoryElements = (state: RootState) => state.translations.newElements.categories;
export const getNewTermElements = (state: RootState) => state.translations.newElements.terms;
export const {
    clearUploadedTerms,
    cleanupTranslations,
    uploadTerms,
    overwriteTerms,
    setMerging,
    setTranslationKeys,
    setSelectedLanguages,
    // setLanguagesOnCleanup,
    setTranslations,
    updateTranslationsWithMerge,
    newTranslationElements,
    resetTranslations,
    setInitialTranslations,
    updateTermProperties,
    updateStatusProperties,
    updateStatusPropertiesAll,
    enableSaveButton,
} = translationsSlice.actions;
export default translationsSlice.reducer;
